import { Component, EventEmitter, inject, OnDestroy, OnInit, Output } from '@angular/core';
import { NgIf } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { MatTableModule } from '@angular/material/table';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { StateNotifyService } from '../../../../common/state/service/state-notify.service';
import { VesselRedundancyWcfdi } from '../../dto/vessel-redundancy-wcfdi';
import { Vessel } from '../../dto/vessel';
import { VesselRedundancyWcfdiDetailsComponent } from '../vessel-redundancy-wcfdi-details/vessel-redundancy-wcfdi-details.component';
import { Subject, takeUntil } from 'rxjs';
import { VesselRedundancyWcfdiService } from '../../service/vessel-redundancy-wcfdi.service';

@Component({
  selector: 'dpi-vessel-redundancy-wcfdi-list',
  imports: [MatCardModule, MatTableModule, MatIconModule, MatButtonModule, MatToolbarModule, MatDialogModule, MatTooltipModule, NgIf],
  templateUrl: './vessel-redundancy-wcfdi-list.component.html',
  styleUrl: './vessel-redundancy-wcfdi-list.component.scss'
})
export class VesselRedundancyWcfdiListComponent implements OnInit, OnDestroy {
  vessel: Vessel;
  displayedColumns: string[] = ['name', 'description'];
  dataSource: VesselRedundancyWcfdi[] = [];
  destroyed$ = new Subject<void>(); // notifier of component destroyed
  @Output('listSizeChanged') listSize = new EventEmitter<number>();
  isReadonly: boolean;
  private _dialog = inject(MatDialog);

  constructor(private notifyService: StateNotifyService, private vesselRedundancyWcfdiService: VesselRedundancyWcfdiService) {
    this.vessel = this.notifyService.getSelectedVessel();
    this.isReadonly = true;
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  ngOnInit(): void {
    this.notifyService.vesselSelection$
      .pipe(takeUntil(this.destroyed$))
      .subscribe(model => {
        this.vessel = model;
        this.reloadVesselRedundancyGroup(model);
      })

    this.notifyService.readonlyMode$
      .pipe(takeUntil(this.destroyed$))
      .subscribe(res => {
        this.isReadonly = res;
      });
  }

  reloadVesselRedundancyGroup(vessel: Vessel) {
    this.vesselRedundancyWcfdiService.getAllRedundancyWCFDIOfVessel(vessel.id)
    .pipe(takeUntil(this.destroyed$))
    .subscribe({
      next: (res: any) => {
        this.dataSource = res;
        this.listSize.emit(res.length);
      },
      error: (error: any) => {
        console.error('Error: ', error);
        this.dataSource = [];
      }
    })
  }

  selectRedundancyGroup(selected: VesselRedundancyWcfdi) {
    if (!this.isReadonly) {
      var objCopy: VesselRedundancyWcfdi = { ...selected };
      const dialogRef = this._dialog.open(VesselRedundancyWcfdiDetailsComponent, { data: objCopy });
      dialogRef.afterClosed()
        .pipe(takeUntil(this.destroyed$))
        .subscribe(result => {
          if (result !== undefined) {
            if (dialogRef.componentInstance.isToUpdate) {
              this.update(dialogRef.componentInstance.redundancyWcfdi);
            }
          } else if (dialogRef.componentInstance.isToDelete) {
            this.delete(dialogRef.componentInstance.redundancyWcfdi.id);
          }
        });
    }
  }
  
   //--- CRUD actions ---
  
    create() {
      this.vesselRedundancyWcfdiService.createRedundancyWCFDI(this.vessel.id)
        .pipe(takeUntil(this.destroyed$))
        .subscribe({
          next: model => {
            this.addNewModelToDataSource(model);
            this.selectRedundancyGroup(model);
          },
          error: err => { console.log(err); }
        });
    }
  
    private addNewModelToDataSource(model: VesselRedundancyWcfdi) {
      let list: VesselRedundancyWcfdi[] = [];
      for (var item of this.dataSource) {
        list.push(item);
      }
      list.push(model);
      this.dataSource = list;
      this.listSize.emit(this.dataSource.length);
    }
  
    update(model: VesselRedundancyWcfdi) {
      this.vesselRedundancyWcfdiService.updateRedundancyWCFDI(model)
        .pipe(takeUntil(this.destroyed$))
        .subscribe({
          next: res => {
            this.replaceModelInDataSource(res);
          },
          error: err => { console.log(err); }
        });
    }
  
    private replaceModelInDataSource(model: VesselRedundancyWcfdi) {
      let list: VesselRedundancyWcfdi[] = [];
      for (var item of this.dataSource) {
        if (item.id === model.id) {
          list.push(model);
        } else {
          list.push(item);
        }
      }
      this.dataSource = list;
      this.listSize.emit(this.dataSource.length);
    }
  
    delete(modelId: number) {
      this.vesselRedundancyWcfdiService.deleteRedundancyWCFDI(modelId)
        .pipe(takeUntil(this.destroyed$))
        .subscribe({
          next: res => {
            this.removeModelFromDataSource(modelId);
          },
          error: err => { console.log(err); }
        });
    }
  
    private removeModelFromDataSource(modelId: number) {
      let list: VesselRedundancyWcfdi[] = [];
      for (var item of this.dataSource) {
        if (item.id === modelId) {
          //the model is to be removed --> do nothing
        } else {
          list.push(item);
        }
      }
      this.dataSource = list;
      this.listSize.emit(this.dataSource.length);
    }
  
}
